import type { GraphElementDefinition } from './types';

// Severity priority for merge conflicts: keep the worst
const SEVERITY_PRIORITY: Record<string, number> = {
  critical: 5,
  high: 4,
  medium: 3,
  low: 2,
  info: 1,
};

interface AbsorbedAlert {
  title: string;
  severity?: string;
  source?: string;
  time?: string;
  mitre?: string;
  originalNodeId: string;
  [key: string]: unknown;
}

function isNode(el: GraphElementDefinition): boolean {
  return el.group === 'nodes' || (!el.group && !el.data.source);
}

function isEdge(el: GraphElementDefinition): boolean {
  return el.group === 'edges' || !!el.data.source;
}

/**
 * Absorbs alert nodes into their parent entity nodes.
 *
 * Rule: If an alert node is connected to exactly ONE non-alert node
 * via any edge, remove the alert node and merge its data into the
 * parent node's properties.
 *
 * Alert nodes connected to 2+ non-alert entities are kept as nodes
 * (they represent structural bridges like lateral movement).
 */
export function absorbAlerts(
  elements: GraphElementDefinition[],
): GraphElementDefinition[] {
  const nodes = elements.filter(isNode);
  const edges = elements.filter(isEdge);

  const alertNodeIds = new Set<string>();
  const alertNodes = new Map<string, GraphElementDefinition>();
  for (const node of nodes) {
    if ((node.data.type as string) === 'alert') {
      alertNodeIds.add(node.data.id as string);
      alertNodes.set(node.data.id as string, node);
    }
  }

  if (alertNodeIds.size === 0) return elements;

  // For each alert node, find connected non-alert entities
  const alertConnections = new Map<string, Set<string>>();

  for (const edge of edges) {
    const src = edge.data.source as string;
    const tgt = edge.data.target as string;

    if (alertNodeIds.has(src) && !alertNodeIds.has(tgt)) {
      const conns = alertConnections.get(src) ?? new Set();
      conns.add(tgt);
      alertConnections.set(src, conns);
    }
    if (alertNodeIds.has(tgt) && !alertNodeIds.has(src)) {
      const conns = alertConnections.get(tgt) ?? new Set();
      conns.add(src);
      alertConnections.set(tgt, conns);
    }
  }

  // Determine which alerts to absorb vs keep
  const absorbableAlerts = new Set<string>();
  const absorptionTargets = new Map<string, string>();

  for (const [alertId, connectedEntities] of alertConnections) {
    if (connectedEntities.size === 1) {
      absorbableAlerts.add(alertId);
      absorptionTargets.set(alertId, Array.from(connectedEntities)[0]!);
    }
  }

  if (absorbableAlerts.size === 0) return elements;

  // Build absorbed alerts per parent node
  const parentAlerts = new Map<string, AbsorbedAlert[]>();

  for (const [alertId, parentId] of absorptionTargets) {
    const alertNode = alertNodes.get(alertId)!;
    const alertData: AbsorbedAlert = {
      title: (alertNode.data.label as string) ?? 'Unknown Alert',
      severity: alertNode.data.severity as string | undefined,
      source: (alertNode.data.source_product as string) ?? undefined,
      time: (alertNode.data.timestamp as string) ?? undefined,
      mitre: (alertNode.data.mitre as string) ?? undefined,
      originalNodeId: alertId,
    };

    const existing = parentAlerts.get(parentId) ?? [];
    existing.push(alertData);
    parentAlerts.set(parentId, existing);
  }

  // Rebuild node list: remove absorbed alerts, enrich parent nodes
  const resultNodes: GraphElementDefinition[] = [];

  for (const node of nodes) {
    const nodeId = node.data.id as string;
    if (absorbableAlerts.has(nodeId)) continue;

    const absorbed = parentAlerts.get(nodeId);
    if (absorbed && absorbed.length > 0) {
      const enriched = { ...node, data: { ...node.data } };
      const existingAlerts =
        (enriched.data.alerts as AbsorbedAlert[] | undefined) ?? [];
      enriched.data.alerts = [...existingAlerts, ...absorbed];
      enriched.data.alertCount = (
        enriched.data.alerts as AbsorbedAlert[]
      ).length;

      let worstSeverity =
        SEVERITY_PRIORITY[enriched.data.severity as string] ?? 0;
      for (const alert of absorbed) {
        const alertSev = SEVERITY_PRIORITY[alert.severity ?? ''] ?? 0;
        if (alertSev > worstSeverity) {
          worstSeverity = alertSev;
          enriched.data.severity = alert.severity;
        }
      }

      resultNodes.push(enriched);
    } else {
      resultNodes.push(node);
    }
  }

  // Rebuild edge list: remove edges involving absorbed alert nodes
  const resultEdges: GraphElementDefinition[] = [];
  for (const edge of edges) {
    const src = edge.data.source as string;
    const tgt = edge.data.target as string;
    if (absorbableAlerts.has(src) || absorbableAlerts.has(tgt)) continue;
    resultEdges.push(edge);
  }

  return [...resultNodes, ...resultEdges];
}

/**
 * Known relationship directions: source type → target type.
 * If an edge has source/target types reversed, flip it.
 */
const EDGE_DIRECTION_MAP: Record<
  string,
  { sourceTypes: string[]; targetTypes: string[] }
> = {
  logged_into: {
    sourceTypes: ['user'],
    targetTypes: ['host'],
  },
  accessed: {
    sourceTypes: ['user'],
    targetTypes: ['application', 'file', 'domain', 'ip'],
  },
  connected_to: {
    sourceTypes: ['ip', 'host'],
    targetTypes: ['ip', 'host'],
  },
  resolved_to: {
    sourceTypes: ['domain'],
    targetTypes: ['ip'],
  },
  spawned: {
    sourceTypes: ['process'],
    targetTypes: ['process'],
  },
  executed: {
    sourceTypes: ['user', 'host'],
    targetTypes: ['process', 'file'],
  },
  downloaded: {
    sourceTypes: ['process', 'host'],
    targetTypes: ['file'],
  },
  member_of: {
    sourceTypes: ['user', 'host'],
    targetTypes: ['group'],
  },
  registered_to: {
    sourceTypes: ['ip', 'domain'],
    targetTypes: ['organization'],
  },
  hosted_by: {
    sourceTypes: ['ip'],
    targetTypes: ['organization', 'domain'],
  },
  located_in: {
    sourceTypes: ['ip', 'host'],
    targetTypes: ['geolocation'],
  },
  in_asn: {
    sourceTypes: ['ip'],
    targetTypes: ['asn'],
  },
  owned_by: {
    sourceTypes: ['asn', 's3_bucket', 'database'],
    targetTypes: ['organization'],
  },
  managed_by: {
    sourceTypes: ['ec2', 'lambda', 's3_bucket', 'rds', 'dynamodb'],
    targetTypes: ['iam_role'],
  },
  assumes: {
    sourceTypes: ['user', 'iam_role', 'ec2', 'lambda'],
    targetTypes: ['iam_role'],
  },
  stores: {
    sourceTypes: ['s3_bucket', 'storage_bucket', 'database', 'rds', 'dynamodb'],
    targetTypes: ['data_finding', 'secret'],
  },
  contains: {
    sourceTypes: ['geolocation', 'organization', 'incident', 'network_zone'],
    targetTypes: ['geolocation', 'asn', 'alert', 'ip', 'host'],
  },
  // Data layer relationships
  reads_writes: {
    sourceTypes: ['application', 'user'],
    targetTypes: ['data_store', 'database', 's3_bucket'],
  },
  classified_as: {
    sourceTypes: ['data_store', 'database'],
    targetTypes: ['data_classification'],
  },
  flows_to: {
    sourceTypes: ['data_store', 'database', 'application'],
    targetTypes: ['data_store', 'database', 'application'],
  },
  hosted_on: {
    sourceTypes: ['application', 'database'],
    targetTypes: ['host', 'ec2', 'cloud_resource'],
  },
  accessed_data: {
    sourceTypes: ['user', 'service_account'],
    targetTypes: ['data_store', 'database', 's3_bucket'],
  },
  // Decisions layer relationships
  annotates: {
    sourceTypes: ['annotation'],
    targetTypes: ['host', 'user', 'ip', 'alert', 'incident', 'vulnerability', 'application'],
  },
  resolves: {
    sourceTypes: ['investigation_outcome'],
    targetTypes: ['incident', 'alert'],
  },
  implicates: {
    sourceTypes: ['investigation_outcome'],
    targetTypes: ['user', 'host', 'ip', 'threat_actor'],
  },
  applied_to: {
    sourceTypes: ['remediation_action'],
    targetTypes: ['host', 'user', 'ip', 'application', 'secret'],
  },
  exempts: {
    sourceTypes: ['exception_exemption'],
    targetTypes: ['vulnerability', 'alert'],
  },
  determined_fp: {
    sourceTypes: ['fp_determination'],
    targetTypes: ['alert'],
  },
  // Threat layer relationships
  exploits: {
    sourceTypes: ['threat_actor', 'campaign'],
    targetTypes: ['vulnerability'],
  },
  uses_technique: {
    sourceTypes: ['threat_actor', 'campaign'],
    targetTypes: ['mitre_technique'],
  },
  attributed_to: {
    sourceTypes: ['campaign', 'incident'],
    targetTypes: ['threat_actor'],
  },
  affects: {
    sourceTypes: ['vulnerability'],
    targetTypes: ['host', 'application', 'cloud_resource'],
  },
};

export function normalizeEdgeDirections(
  elements: GraphElementDefinition[],
): GraphElementDefinition[] {
  const nodes = elements.filter(isNode);
  const edges = elements.filter(isEdge);

  const nodeTypes = new Map<string, string>();
  for (const n of nodes) {
    nodeTypes.set(n.data.id as string, (n.data.type as string) ?? '');
  }

  const fixedEdges = edges.map((edge) => {
    const edgeType =
      (edge.data.label as string)?.replace(/\s/g, '_') ?? '';
    const rule = EDGE_DIRECTION_MAP[edgeType];
    if (!rule) return edge;

    const srcType = nodeTypes.get(edge.data.source as string) ?? '';
    const tgtType = nodeTypes.get(edge.data.target as string) ?? '';

    const srcCorrect = rule.sourceTypes.includes(srcType);
    const tgtCorrect = rule.targetTypes.includes(tgtType);
    const srcReversed = rule.targetTypes.includes(srcType);
    const tgtReversed = rule.sourceTypes.includes(tgtType);

    if (!srcCorrect && !tgtCorrect && srcReversed && tgtReversed) {
      return {
        ...edge,
        data: {
          ...edge.data,
          id: `${edge.data.target}-${edgeType}-${edge.data.source}`,
          source: edge.data.target,
          target: edge.data.source,
        },
      };
    }
    return edge;
  });

  return [...nodes, ...fixedEdges];
}

// Type specificity: more specific types win during dedup merging
const TYPE_SPECIFICITY: Record<string, number> = {
  // Compute
  host: 10,
  ec2: 10,
  lambda: 10,
  k8s_pod: 9,
  k8s_node: 9,
  container: 9,
  eks_cluster: 9,
  aks_cluster: 9,
  gke_cluster: 9,
  auto_scaling_group: 8,
  process: 8,

  // Identity
  user: 10,
  iam_role: 9,
  certificate: 8,
  secret: 8,
  group: 8,

  // Findings
  alert: 10,
  incident: 10,
  threat_actor: 10,
  vulnerability: 9,
  campaign: 9,

  // Data
  file: 8,
  s3_bucket: 9,
  storage_bucket: 9,
  database: 9,
  rds: 9,
  dynamodb: 9,
  data_finding: 8,

  // Network
  security_group: 8,
  network_zone: 7,
  asn: 7,
  domain: 6,
  url: 6,
  ip: 5,
  internet: 10, // canonical — should never be merged

  // Identity (new)
  service_account: 9,
  api_key_token: 8,

  // Application
  application: 8,
  email: 7,
  cloud_resource: 8,
  api_endpoint: 7,
  container_workload: 8,

  // Data (new)
  data_store: 8,
  data_classification: 7,
  data_flow: 7,

  // Findings (new)
  indicator_ioc: 9,
  mitre_technique: 8,

  // Decisions
  annotation: 7,
  investigation_outcome: 8,
  remediation_action: 8,
  exception_exemption: 8,
  fp_determination: 8,

  // Context
  organization: 8,
  geolocation: 7,

  // Framework (legacy)
  policy: 8,
};

function canonicalKey(node: GraphElementDefinition): string {
  return ((node.data.label as string) ?? '').toLowerCase().trim();
}

/**
 * Deduplicates graph elements by canonical label value.
 * When two nodes share the same canonical label:
 * - Keep the more specific type
 * - Keep the highest severity
 * - Merge properties (union)
 * - Rewrite all edges referencing the merged-away ID
 * - Add an 'aliases' property listing merged-away IDs
 */
export function deduplicateGraphElements(
  elements: GraphElementDefinition[],
): GraphElementDefinition[] {
  const nodes = elements.filter(isNode);
  const edges = elements.filter(isEdge);

  // Group nodes by canonical key
  const canonicalGroups = new Map<string, GraphElementDefinition[]>();
  for (const node of nodes) {
    const key = canonicalKey(node);
    if (!key) continue;
    const group = canonicalGroups.get(key) ?? [];
    group.push(node);
    canonicalGroups.set(key, group);
  }

  // Merge each group into a single surviving node
  const idRemapping = new Map<string, string>();
  const mergedNodes: GraphElementDefinition[] = [];

  for (const [, group] of canonicalGroups) {
    if (group.length === 1) {
      mergedNodes.push(group[0]!);
      continue;
    }

    // Sort by type specificity (desc), then severity (desc)
    group.sort((a, b) => {
      const typeA = TYPE_SPECIFICITY[a.data.type as string] ?? 0;
      const typeB = TYPE_SPECIFICITY[b.data.type as string] ?? 0;
      if (typeB !== typeA) return typeB - typeA;
      const sevA = SEVERITY_PRIORITY[a.data.severity as string] ?? 0;
      const sevB = SEVERITY_PRIORITY[b.data.severity as string] ?? 0;
      return sevB - sevA;
    });

    const survivor = { ...group[0]!, data: { ...group[0]!.data } };
    const aliases: string[] = [];

    for (let i = 1; i < group.length; i++) {
      const other = group[i]!;
      aliases.push(other.data.id as string);
      idRemapping.set(other.data.id as string, survivor.data.id as string);

      // Take highest severity
      const survSev =
        SEVERITY_PRIORITY[survivor.data.severity as string] ?? 0;
      const otherSev =
        SEVERITY_PRIORITY[other.data.severity as string] ?? 0;
      if (otherSev > survSev) {
        survivor.data.severity = other.data.severity;
      }

      // Merge properties (union, don't overwrite existing)
      for (const [key, val] of Object.entries(other.data)) {
        if (
          !['id', 'label', 'type', 'severity'].includes(key) &&
          !(key in survivor.data)
        ) {
          survivor.data[key] = val;
        }
      }
    }

    if (aliases.length > 0) {
      survivor.data.aliases = aliases;
    }
    mergedNodes.push(survivor);
  }

  // Rewrite edge references and deduplicate edges
  const rewrittenEdges: GraphElementDefinition[] = [];
  const edgeDedup = new Set<string>();

  for (const edge of edges) {
    const src =
      idRemapping.get(edge.data.source as string) ??
      (edge.data.source as string);
    const tgt =
      idRemapping.get(edge.data.target as string) ??
      (edge.data.target as string);

    // Skip self-loops created by merging
    if (src === tgt) continue;

    const edgeKey = `${src}-${edge.data.label ?? ''}-${tgt}`;
    if (edgeDedup.has(edgeKey)) continue;
    edgeDedup.add(edgeKey);

    rewrittenEdges.push({
      ...edge,
      data: {
        ...edge.data,
        id: `${src}-${(edge.data as Record<string, unknown>).type ?? 'rel'}-${tgt}`,
        source: src,
        target: tgt,
      },
    });
  }

  return [...mergedNodes, ...rewrittenEdges];
}
